import {testIdProps} from '@github-ui/test-id-props'
import {FocusKeys} from '@primer/behaviors'
import {AnchoredOverlay, PointerBox} from '@primer/react'
import {useCallback, useEffect, useState, useSyncExternalStore} from 'react'

import {getInitialState} from '../../helpers/initial-state'

type UserNoticePopoverProps = {
  anchorRef: React.MutableRefObject<HTMLDivElement | null>
  children: React.ReactNode
  imageToLoad?: string
}

export const UserNoticePopover = ({anchorRef, children, imageToLoad}: UserNoticePopoverProps) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const {showTemplateDialog} = getInitialState()

  const subscribe = useCallback(
    (notify: () => void) => {
      if (!anchorRef.current) return () => undefined
      const observer = new MutationObserver(notify)
      observer.observe(anchorRef.current, {
        attributes: true,
        characterData: false,
        childList: false,
        subtree: true,
      })
      return () => {
        observer.disconnect()
      }
    },
    [anchorRef],
  )

  // The display of the anchor ref for the view options menu is periodically toggled so that it can be positioned
  // appropriately after rendering or resizing the page. This ensures that the popover is displayed only when the anchor is visible.
  const getSnapshot = useCallback(() => {
    return Boolean(anchorRef.current && anchorRef.current.style.display !== 'none')
  }, [anchorRef])

  const isVisible = useSyncExternalStore(subscribe, getSnapshot)

  // Optionally wait for an image to load before displaying the popover
  useEffect(() => {
    if (!imageToLoad) return

    const img = document.createElement('img')
    img.src = imageToLoad
    img.onload = function () {
      setImageLoaded(true)
    }
  }, [imageToLoad, setImageLoaded])

  // Because of z-index issues with multiple portals, avoid showing user-notice for brand new projects
  if (showTemplateDialog) return null
  if (!isVisible) return null
  if (imageToLoad && !imageLoaded) return null

  return (
    <AnchoredOverlay
      open
      renderAnchor={null}
      align="start"
      anchorOffset={12}
      alignmentOffset={-8}
      anchorRef={anchorRef}
      overlayProps={{
        sx: {overflow: 'visible'},
        ...testIdProps('user-notice-popover'),
      }}
      focusTrapSettings={{disabled: true}}
      focusZoneSettings={{bindKeys: FocusKeys.Tab, containerRef: anchorRef}}
    >
      <PointerBox
        caret="top-left"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: 1,
          justifyContent: 'space-between',
          padding: 4,
          width: '326px',
        }}
      >
        {children}
      </PointerBox>
    </AnchoredOverlay>
  )
}

try{ UserNoticePopover.displayName ||= 'UserNoticePopover' } catch {}